import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { AlertCircle, Loader2, Percent, RefreshCw, RotateCcw, ShieldCheck, Sparkles } from 'lucide-react'
import toast from 'react-hot-toast'
import { feesApi, classesApi } from '../api/services.js'
import ActionOtpModal from '../components/ActionOtpModal.jsx'
import PageHeader from '../components/PageHeader.jsx'
import { formatAcademicYear, getAcademicYearOptions, isAcademicYear } from '../utils/academicYear.js'
import { formatCurrency, formatDate } from '../utils/fee.js'

const adjustmentTypes = [
  {
    value: 'concession',
    label: 'Concession',
    description: 'Merit or hardship waiver',
    color: 'border-blue-200 bg-blue-50 text-blue-700',
    accent: 'text-blue-600',
    bg: 'bg-blue-50',
  },
  {
    value: 'discount',
    label: 'Discount',
    description: 'General fee reduction',
    color: 'border-green-200 bg-green-50 text-green-700',
    accent: 'text-green-600',
    bg: 'bg-green-50',
  },
  {
    value: 'fine',
    label: 'Fine',
    description: 'Late payment penalty',
    color: 'border-red-200 bg-red-50 text-red-700',
    accent: 'text-red-600',
    bg: 'bg-red-50',
  },
]

const PAYMENT_EPSILON = 0.01
const getNumber = (value) => Number(value || 0)
const getEntityId = (value) => value?._id || value?.id || value || ''
const getStudentName = (account) =>
  [account?.studentId?.firstName, account?.studentId?.lastName].filter(Boolean).join(' ').trim() || account?.studentId?.name || 'Student'
const classLabel = (item) => `${item?.name || 'Class'}${item?.section ? ` - ${item.section}` : ''}`
const getInstallmentNetAmount = (installment) =>
  Math.max(
    0,
    getNumber(installment?.amount) -
      getNumber(installment?.discount) -
      getNumber(installment?.concession) +
      getNumber(installment?.fine)
  )
const getInstallmentBalance = (installment) => Math.max(0, getInstallmentNetAmount(installment) - getNumber(installment?.paidAmount))

export default function FeeDiscountsPage() {
  const [searchParams] = useSearchParams()
  const [accounts, setAccounts] = useState([])
  const [classes, setClasses] = useState([])
  const [loading, setLoading] = useState(true)
  const [accountSearch, setAccountSearch] = useState('')
  const [mode, setMode] = useState(searchParams.get('bulk') === 'true' ? 'bulk' : 'single')
  const [action, setAction] = useState('apply')
  const [scope, setScope] = useState('class')
  const [saving, setSaving] = useState(false)
  const [loadingInstallments, setLoadingInstallments] = useState(false)
  const [account, setAccount] = useState(null)
  const [selectedInstallments, setSelectedInstallments] = useState([])
  const [showOtpModal, setShowOtpModal] = useState(false)
  const [pendingBulkPayload, setPendingBulkPayload] = useState(null)
  const routeInstallmentIds = useMemo(
    () => String(searchParams.get('installmentIds') || '').split(',').map((item) => item.trim()).filter(Boolean),
    [searchParams]
  )
  const routeMonthLabels = useMemo(
    () => String(searchParams.get('selectedMonthLabels') || '').split(',').map((item) => item.trim()).filter(Boolean),
    [searchParams]
  )
  const academicYearOptions = useMemo(() => getAcademicYearOptions({ pastCount: 1, futureCount: 2 }), [])
  const [form, setForm] = useState({
    accountId: searchParams.get('accountId') || '',
    classId: '',
    academicYear: formatAcademicYear(searchParams.get('academicYear') || ''),
    type: 'concession',
    valueType: 'flat',
    value: '',
    reason: '',
  })

  const fetchData = async () => {
    try {
      setLoading(true)
      const [accountsRes, classesRes] = await Promise.all([
        feesApi.getAccounts(),
        classesApi.getAll(),
      ])
      setAccounts(Array.isArray(accountsRes) ? accountsRes : [])
      setClasses(Array.isArray(classesRes) ? classesRes : [])
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load fee accounts')
      setAccounts([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  useEffect(() => {
    if (mode === 'bulk' && action === 'reverse') setAction('apply')
  }, [action, mode])

  useEffect(() => {
    let active = true
    if (mode !== 'single' || !form.accountId) {
      setAccount(null)
      setSelectedInstallments([])
      return undefined
    }

    const load = async () => {
      try {
        setLoadingInstallments(true)
        const data = await feesApi.getInstallments(form.accountId)
        if (!active) return
        setAccount(data || null)
      } catch (err) {
        if (active) {
          setAccount(null)
          toast.error(err.response?.data?.message || 'Failed to load installments')
        }
      } finally {
        if (active) setLoadingInstallments(false)
      }
    }

    load()
    return () => { active = false }
  }, [form.accountId, mode])

  useEffect(() => {
    if (!routeInstallmentIds.length || !account?.installments?.length || selectedInstallments.length > 0) return
    const availableIds = new Set(account.installments.map(getEntityId).filter(Boolean))
    setSelectedInstallments(routeInstallmentIds.filter((id) => availableIds.has(id)))
  }, [account, routeInstallmentIds, selectedInstallments.length])

  const filteredAccounts = useMemo(() => {
    const query = accountSearch.trim().toLowerCase()
    return accounts.filter((item) => {
      if (!query) return true
      const name = getStudentName(item).toLowerCase()
      const admissionNo = item.studentId?.admissionNo?.toLowerCase() || ''
      const cls = classLabel(item.classId).toLowerCase()
      const year = formatAcademicYear(item.academicYear).toLowerCase()
      return name.includes(query) || admissionNo.includes(query) || cls.includes(query) || year.includes(query)
    })
  }, [accountSearch, accounts])

  const selectedType = adjustmentTypes.find((item) => item.value === form.type) || adjustmentTypes[0]
  const selectedAccount = accounts.find((item) => getEntityId(item) === form.accountId)
  const accountInstallments = account?.installments || []
  const targetInstallments = selectedInstallments.length
    ? accountInstallments.filter((item) => selectedInstallments.includes(getEntityId(item)))
    : accountInstallments

  const reversePreview = useMemo(() => {
    if (mode !== 'single' || !account) {
      return {
        currentTotal: 0,
        reversibleTotal: 0,
        lockedTotal: 0,
        eligibleCount: 0,
        reopenedDueTotal: 0,
        settledAffectedCount: 0,
      }
    }

    const adjustmentField = form.type === 'discount' ? 'discount' : form.type === 'concession' ? 'concession' : 'fine'
    let currentTotal = 0
    let reversibleTotal = 0
    let eligibleCount = 0
    let reopenedDueTotal = 0
    let settledAffectedCount = 0

    targetInstallments.forEach((installment) => {
      const currentAdjustment = getNumber(installment?.[adjustmentField])
      if (currentAdjustment <= PAYMENT_EPSILON) return

      currentTotal += currentAdjustment
      const balance = getInstallmentBalance(installment)
      const removableAmount = form.type === 'fine' ? Math.min(currentAdjustment, balance) : currentAdjustment

      if (removableAmount <= PAYMENT_EPSILON) return
      reversibleTotal += removableAmount
      eligibleCount += 1
      if (form.type !== 'fine' && balance <= PAYMENT_EPSILON) {
        reopenedDueTotal += removableAmount
        settledAffectedCount += 1
      }
    })

    const normalizedCurrent = Number(currentTotal.toFixed(2))
    const normalizedReversible = Number(reversibleTotal.toFixed(2))
    return {
      currentTotal: normalizedCurrent,
      reversibleTotal: normalizedReversible,
      lockedTotal: Number((normalizedCurrent - normalizedReversible).toFixed(2)),
      eligibleCount,
      reopenedDueTotal: Number(reopenedDueTotal.toFixed(2)),
      settledAffectedCount,
    }
  }, [account, form.type, mode, targetInstallments])

  const toggleInstallment = (id) => {
    setSelectedInstallments((prev) => prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id])
  }

  const resetAfterSave = () => {
    setForm((prev) => ({
      ...prev,
      value: '',
      reason: '',
      classId: mode === 'bulk' && scope === 'school' ? '' : prev.classId,
    }))
    setSelectedInstallments([])
    fetchData()
  }

  const buildPayload = () => {
    const numericValue = Number(form.value)
    if (mode === 'single' && !form.accountId) throw new Error('Choose a fee account first')
    if (!form.reason.trim()) throw new Error('Reason is required')

    if (action === 'apply') {
      if (!numericValue || numericValue <= 0) throw new Error('Enter a valid positive value')
      if (form.valueType === 'percent' && numericValue > 100) throw new Error('Percentage cannot be more than 100')
    } else if (reversePreview.reversibleTotal <= PAYMENT_EPSILON) {
      throw new Error(form.type === 'fine' ? 'No eligible unpaid fine amount is available to reverse' : 'No saved adjustments are available to reverse for this type')
    }

    if (mode === 'bulk') {
      if (scope === 'class' && !form.classId) throw new Error('Choose a class for bulk adjustment')
      if (form.academicYear && !isAcademicYear(form.academicYear)) throw new Error('Use academic year format YYYY-YYYY')
      return {
        scope,
        classId: scope === 'class' ? form.classId : undefined,
        type: form.type,
        valueType: form.valueType,
        value: numericValue,
        reason: form.reason.trim(),
        academicYear: form.academicYear ? formatAcademicYear(form.academicYear) : undefined,
      }
    }

    return {
      accountId: form.accountId,
      type: form.type,
      valueType: form.valueType,
      value: numericValue,
      installmentIds: selectedInstallments.length ? selectedInstallments : undefined,
      reason: form.reason.trim(),
    }
  }

  const submitSingle = async (payload) => {
    if (action === 'reverse') {
      await feesApi.reverseDiscount(payload.accountId, {
        type: payload.type,
        installmentIds: payload.installmentIds,
        reason: payload.reason,
      })
    } else {
      await feesApi.applyDiscount(payload.accountId, {
        type: payload.type,
        valueType: payload.valueType,
        value: payload.value,
        installmentIds: payload.installmentIds,
        reason: payload.reason,
      })
    }
  }

  const submitBulk = async (payload, actionToken) => {
    await feesApi.bulkApplyDiscount({ ...payload, actionToken })
  }

  const handleSubmit = async () => {
    let payload
    try {
      payload = buildPayload()
    } catch (err) {
      toast.error(err.message)
      return
    }

    const scopeLabel = mode === 'single'
      ? getStudentName(selectedAccount)
      : scope === 'class'
      ? 'entire class'
      : 'all students'
    const confirmText = action === 'apply'
      ? `Apply ${form.valueType === 'flat' ? formatCurrency(payload.value) : `${payload.value}%`} ${selectedType.label.toLowerCase()} to ${scopeLabel}?\n\nReason: ${payload.reason}`
      : [
          `Reverse ${formatCurrency(reversePreview.reversibleTotal)} of ${selectedType.label.toLowerCase()} from ${reversePreview.eligibleCount} installment${reversePreview.eligibleCount === 1 ? '' : 's'} for ${scopeLabel}?`,
          reversePreview.reopenedDueTotal > PAYMENT_EPSILON
            ? `This will reopen ${formatCurrency(reversePreview.reopenedDueTotal)} as due on ${reversePreview.settledAffectedCount} settled installment${reversePreview.settledAffectedCount === 1 ? '' : 's'}.`
            : null,
          `Reason: ${payload.reason}`,
        ].filter(Boolean).join('\n\n')

    if (!window.confirm(confirmText)) return

    if (mode === 'bulk') {
      setPendingBulkPayload(payload)
      setShowOtpModal(true)
      return
    }

    try {
      setSaving(true)
      await submitSingle(payload)
      toast.success(action === 'reverse' ? `${selectedType.label} reversed successfully` : `${selectedType.label} applied successfully`)
      resetAfterSave()
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not apply adjustment')
    } finally {
      setSaving(false)
    }
  }

  const handleOtpVerified = async (actionToken) => {
    if (!pendingBulkPayload) return
    try {
      setShowOtpModal(false)
      setSaving(true)
      await submitBulk(pendingBulkPayload, actionToken)
      toast.success(`${selectedType.label} applied successfully`)
      setPendingBulkPayload(null)
      resetAfterSave()
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not apply bulk adjustment')
    } finally {
      setSaving(false)
    }
  }

  const pendingInstallments = accountInstallments.filter((item) => item.status !== 'paid')
  const adjustmentInstallments = accountInstallments.filter((item) => getNumber(item.discount) > 0 || getNumber(item.concession) > 0 || getNumber(item.fine) > 0)
  const visibleInstallments = action === 'reverse' ? adjustmentInstallments : pendingInstallments

  return (
    <div className="space-y-5">
      <PageHeader
        title="Fee Adjustments"
        subtitle="Apply concessions, discounts, and fines, or reverse them using the same admin rules as Edubridge RN."
        chips={[
          `${accounts.length} fee accounts`,
          `${classes.length} class sections`,
          mode === 'bulk' ? `Bulk ${scope}` : `Single account • ${action}`,
        ]}
        actions={
          <button onClick={fetchData} className="btn-secondary p-2 self-start sm:self-auto" title="Refresh fee data">
            <RefreshCw className="w-4 h-4" />
          </button>
        }
      />

      <div className="card p-4 grid gap-3 lg:grid-cols-2">
        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-2">Apply Mode</label>
          <div className="grid grid-cols-2 gap-3">
            {[
              { value: 'single', label: 'Single Account' },
              { value: 'bulk', label: 'Bulk Adjustment' },
            ].map((option) => (
              <button
                key={option.value}
                onClick={() => setMode(option.value)}
                className={`rounded-2xl border px-4 py-3 text-sm font-semibold transition-colors ${
                  mode === option.value ? 'border-primary-600 bg-primary-50 text-primary-700' : 'border-gray-200 text-gray-600 hover:border-primary-200'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {mode === 'single' && (
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-2">Action</label>
            <div className="grid grid-cols-2 gap-3">
              {[
                { value: 'apply', label: 'Apply Adjustment', icon: Percent },
                { value: 'reverse', label: 'Reverse Adjustment', icon: RotateCcw },
              ].map((option) => {
                const Icon = option.icon
                return (
                  <button
                    key={option.value}
                    onClick={() => setAction(option.value)}
                    className={`flex items-center justify-center gap-2 rounded-2xl border px-4 py-3 text-sm font-semibold transition-colors ${
                      action === option.value ? 'border-primary-600 bg-primary-50 text-primary-700' : 'border-gray-200 text-gray-600 hover:border-primary-200'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {option.label}
                  </button>
                )
              })}
            </div>
          </div>
        )}
      </div>

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_340px]">
        <div className="space-y-5">
          <div className="card p-5 space-y-5">
            {mode === 'single' ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Find Student Account</label>
                  <input
                    value={accountSearch}
                    onChange={(event) => setAccountSearch(event.target.value)}
                    className="input-field w-full"
                    placeholder="Search by student, admission no, class, or year..."
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Fee Account</label>
                  <select
                    className="input-field w-full"
                    value={form.accountId}
                    onChange={(event) => {
                      setSelectedInstallments([])
                      setForm((prev) => ({ ...prev, accountId: event.target.value }))
                    }}
                  >
                    <option value="">Select account</option>
                    {filteredAccounts.map((item) => (
                      <option key={item._id || item.id} value={item._id || item.id}>
                        {getStudentName(item)}
                        {item.studentId?.admissionNo ? ` • ${item.studentId.admissionNo}` : ''}
                        {item.classId?.name ? ` • ${classLabel(item.classId)}` : ''}
                        {item.academicYear ? ` • ${formatAcademicYear(item.academicYear)}` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                {form.accountId && (
                  <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
                    {routeMonthLabels.length > 0 && (
                      <div className="mb-4 rounded-xl border border-primary-100 bg-primary-50 px-4 py-3 text-sm font-medium text-primary-700">
                        This adjustment is scoped to: {routeMonthLabels.join(', ')}
                      </div>
                    )}
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600">Target Installments</label>
                        <p className="text-xs text-gray-500 mt-1">
                          {action === 'reverse'
                            ? 'Optional: select adjusted installments to limit the reversal.'
                            : 'Optional: leave unselected to apply across all pending installments.'}
                        </p>
                      </div>
                      {selectedInstallments.length > 0 && (
                        <button onClick={() => setSelectedInstallments([])} className="text-xs font-semibold text-primary-600 hover:text-primary-700">Clear</button>
                      )}
                    </div>
                    <div className="mt-3 space-y-2">
                      {loadingInstallments ? (
                        <div className="flex items-center justify-center py-6">
                          <Loader2 className="w-5 h-5 animate-spin text-gray-300" />
                        </div>
                      ) : visibleInstallments.length === 0 ? (
                        <div className="rounded-xl border border-dashed border-gray-200 bg-white px-4 py-5 text-center text-sm text-gray-500">
                          {action === 'reverse' ? 'No adjusted installments available on this account.' : 'No pending installments available on this account.'}
                        </div>
                      ) : (
                        visibleInstallments.map((item) => {
                          const installmentId = getEntityId(item)
                          const isSelected = selectedInstallments.includes(installmentId)
                          const overdue = item.dueDate && new Date(item.dueDate) < new Date()
                          const currentAdjustment = getNumber(item.discount) + getNumber(item.concession) + getNumber(item.fine)
                          return (
                            <button
                              key={installmentId}
                              onClick={() => toggleInstallment(installmentId)}
                              className={`w-full rounded-xl border px-4 py-3 text-left transition-colors ${
                                isSelected ? 'border-primary-300 bg-primary-50' : 'border-gray-200 bg-white hover:border-primary-200 hover:bg-primary-50/40'
                              }`}
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div>
                                  <p className="font-medium text-gray-900">{item.label || item.componentName || 'Installment'}</p>
                                  <p className="text-xs text-gray-500 mt-1">Due {formatDate(item.dueDate)}</p>
                                  {action === 'reverse' && currentAdjustment > 0 && (
                                    <p className="text-xs text-gray-400 mt-1">
                                      Discount {formatCurrency(item.discount || 0)} · Concession {formatCurrency(item.concession || 0)} · Fine {formatCurrency(item.fine || 0)}
                                    </p>
                                  )}
                                </div>
                                <div className="text-right">
                                  <p className="font-semibold text-gray-900">{formatCurrency(item.amount || 0)}</p>
                                  <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold mt-2 ${overdue ? 'bg-red-50 text-red-600' : 'bg-slate-100 text-slate-600'}`}>
                                    {overdue ? 'Overdue' : item.status || 'Pending'}
                                  </span>
                                </div>
                              </div>
                            </button>
                          )
                        })
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-2">Apply To</label>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {[
                      { value: 'class', label: 'A Class' },
                      { value: 'school', label: 'All Students' },
                    ].map((option) => (
                      <button
                        key={option.value}
                        onClick={() => setScope(option.value)}
                        className={`rounded-2xl border px-4 py-4 text-left font-semibold transition-colors ${
                          scope === option.value ? 'border-primary-600 bg-primary-50 text-primary-700' : 'border-gray-200 text-gray-700 hover:border-primary-200'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
                {scope === 'class' && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Select Class</label>
                    <select className="input-field w-full" value={form.classId} onChange={(event) => setForm((prev) => ({ ...prev, classId: event.target.value }))}>
                      <option value="">Select class</option>
                      {classes.map((cls) => (
                        <option key={cls._id || cls.id} value={cls._id || cls.id}>{classLabel(cls)}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Academic Year (optional filter)</label>
                  <input
                    value={form.academicYear}
                    onChange={(event) => setForm((prev) => ({ ...prev, academicYear: event.target.value }))}
                    onBlur={() => setForm((prev) => ({ ...prev, academicYear: formatAcademicYear(prev.academicYear) }))}
                    className="input-field w-full"
                    placeholder="e.g. 2026-2027"
                  />
                  <div className="mt-3 flex flex-wrap gap-2">
                    {academicYearOptions.map((year) => (
                      <button
                        key={year}
                        onClick={() => setForm((prev) => ({ ...prev, academicYear: year }))}
                        className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${
                          form.academicYear === year ? 'border-primary-600 bg-primary-50 text-primary-700' : 'border-gray-200 text-gray-600 hover:border-primary-200'
                        }`}
                      >
                        {year}
                      </button>
                    ))}
                    {form.academicYear && (
                      <button onClick={() => setForm((prev) => ({ ...prev, academicYear: '' }))} className="rounded-full border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-600">
                        Clear
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-2">Type</label>
              <div className="grid md:grid-cols-3 gap-3">
                {adjustmentTypes.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setForm((prev) => ({ ...prev, type: option.value }))}
                    className={`rounded-2xl border px-4 py-4 text-left transition-colors ${form.type === option.value ? option.color : 'border-gray-200 hover:border-primary-200'}`}
                  >
                    <p className={`font-semibold ${form.type === option.value ? option.accent : 'text-gray-800'}`}>{option.label}</p>
                    <p className="text-sm text-gray-500 mt-1">{option.description}</p>
                  </button>
                ))}
              </div>
            </div>

            {action === 'reverse' && mode === 'single' && (
              <div className={`rounded-2xl border px-4 py-4 ${selectedType.color}`}>
                <div className="flex items-start gap-3">
                  {loadingInstallments ? <Loader2 className="w-5 h-5 animate-spin" /> : <RotateCcw className={`w-5 h-5 mt-0.5 ${selectedType.accent}`} />}
                  <div className="flex-1">
                    <p className={`font-semibold ${selectedType.accent}`}>Reverse Preview</p>
                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                      <PreviewStat label={`Current ${selectedType.label}`} value={formatCurrency(reversePreview.currentTotal)} />
                      <PreviewStat label="Will Reverse Now" value={formatCurrency(reversePreview.reversibleTotal)} />
                      <PreviewStat label="Eligible Installments" value={reversePreview.eligibleCount} />
                      {reversePreview.reopenedDueTotal > PAYMENT_EPSILON && <PreviewStat label="Reopens Due Amount" value={formatCurrency(reversePreview.reopenedDueTotal)} danger />}
                      {reversePreview.lockedTotal > PAYMENT_EPSILON && <PreviewStat label="Locked By Paid Balance" value={formatCurrency(reversePreview.lockedTotal)} warn />}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {action === 'apply' && (
              <div className="grid md:grid-cols-[220px_minmax(0,1fr)] gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Value Type</label>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { value: 'flat', label: 'Flat ₹' },
                      { value: 'percent', label: 'Percent %' },
                    ].map((option) => (
                      <button
                        key={option.value}
                        onClick={() => setForm((prev) => ({ ...prev, valueType: option.value }))}
                        className={`rounded-xl border px-3 py-2 text-sm font-semibold transition-colors ${
                          form.valueType === option.value ? 'border-primary-600 bg-primary-50 text-primary-700' : 'border-gray-200 text-gray-600 hover:border-primary-200'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Amount</label>
                  <input type="number" value={form.value} onChange={(event) => setForm((prev) => ({ ...prev, value: event.target.value }))} className="input-field w-full" placeholder={form.valueType === 'flat' ? 'Enter rupee amount' : 'Enter percentage'} />
                </div>
              </div>
            )}

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Reason *</label>
              <textarea
                value={form.reason}
                onChange={(event) => setForm((prev) => ({ ...prev, reason: event.target.value }))}
                className="input-field w-full h-28 resize-none"
                placeholder={
                  action === 'reverse'
                    ? 'e.g. Applied by mistake, policy updated'
                    : form.type === 'concession'
                    ? 'e.g. Merit scholarship, financial hardship'
                    : form.type === 'fine'
                    ? 'e.g. Late payment for April'
                    : 'e.g. Sibling discount, staff child benefit'
                }
              />
            </div>

            <div className={`rounded-2xl border px-4 py-4 ${selectedType.color}`}>
              <div className="flex items-start gap-3">
                <Sparkles className={`w-5 h-5 mt-0.5 ${selectedType.accent}`} />
                <div>
                  <p className={`font-semibold ${selectedType.accent}`}>{selectedType.label} note</p>
                  <p className="text-sm mt-1">
                    {action === 'reverse'
                      ? form.type === 'fine'
                        ? 'Fine reversal is limited by the remaining unpaid balance. Refunds or credits are not created automatically.'
                        : 'Reversing a saved discount or concession can reopen outstanding due even on settled installments.'
                      : form.type === 'fine'
                      ? 'Fines increase the outstanding amount and appear as a debit on receipts.'
                      : `${selectedType.label}s reduce payable fees and are recorded on receipts.`}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <button onClick={handleSubmit} disabled={saving} className="btn-primary flex items-center gap-2 px-5">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : action === 'reverse' ? <RotateCcw className="w-4 h-4" /> : <Percent className="w-4 h-4" />}
                {action === 'reverse' ? `Reverse ${selectedType.label}` : `Apply ${selectedType.label}`}
              </button>
            </div>
          </div>
        </div>

        <div className="space-y-5">
          <div className="card p-5">
            <h3 className="font-semibold text-gray-900">Adjustment Preview</h3>
            <div className="mt-4 space-y-3 text-sm">
              <InfoRow label="Mode" value={mode === 'single' ? `Single • ${action}` : `Bulk • ${scope}`} />
              <InfoRow label="Type" value={selectedType.label} className={selectedType.accent} />
              {action === 'apply' && (
                <InfoRow
                  label="Value"
                  value={form.value ? (form.valueType === 'flat' ? formatCurrency(form.value) : `${form.value}%`) : '—'}
                />
              )}
              {mode === 'bulk' && <InfoRow label="Academic Year" value={form.academicYear || 'All years'} />}
              {mode === 'single' && <InfoRow label="Target installments" value={selectedInstallments.length ? `${selectedInstallments.length} selected` : action === 'reverse' ? 'All adjusted' : 'All pending'} />}
            </div>
            {mode === 'bulk' && (
              <div className="mt-5 rounded-xl border border-amber-100 bg-amber-50 p-4 text-sm text-amber-800">
                <div className="flex items-start gap-3">
                  <ShieldCheck className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <p>Bulk adjustments require email OTP verification before the backend accepts the action token.</p>
                </div>
              </div>
            )}
          </div>

          <div className="card p-5">
            <h3 className="font-semibold text-gray-900 mb-4">Selected Account</h3>
            {loading ? (
              <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-gray-300" /></div>
            ) : mode !== 'single' ? (
              <p className="text-sm text-gray-500">Bulk mode applies to many accounts.</p>
            ) : !selectedAccount ? (
              <p className="text-sm text-gray-500">Pick a fee account to see current totals.</p>
            ) : (
              <div className="space-y-4">
                <div>
                  <p className="font-semibold text-gray-900">{getStudentName(selectedAccount)}</p>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {classLabel(selectedAccount.classId)}
                    {selectedAccount.studentId?.admissionNo ? ` • ${selectedAccount.studentId.admissionNo}` : ''}
                    {selectedAccount.academicYear ? ` • ${formatAcademicYear(selectedAccount.academicYear)}` : ''}
                  </p>
                </div>
                <div className="rounded-xl bg-gray-50 p-4 space-y-3 text-sm">
                  <InfoRow label="Total Fee" value={formatCurrency(selectedAccount.totalAmount || 0)} />
                  <InfoRow label="Collected" value={formatCurrency(selectedAccount.paidAmount || 0)} />
                  <InfoRow label="Outstanding" value={formatCurrency(selectedAccount.outstanding || 0)} className="text-red-600" />
                  <InfoRow label="Status" value={selectedAccount.status || 'pending'} />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <ActionOtpModal
        open={showOtpModal}
        scope="fee_adjustment"
        actionLabel="bulk fee adjustment"
        onVerified={handleOtpVerified}
        onClose={() => setShowOtpModal(false)}
      />
    </div>
  )
}

function InfoRow({ label, value, className = 'text-gray-900' }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-gray-500">{label}</span>
      <span className={`font-medium text-right ${className}`}>{value}</span>
    </div>
  )
}

function PreviewStat({ label, value, danger = false, warn = false }) {
  return (
    <div className={`rounded-xl px-3 py-2 ${danger ? 'bg-red-100' : warn ? 'bg-amber-100' : 'bg-white/70'}`}>
      <p className={`text-sm font-semibold ${danger ? 'text-red-700' : warn ? 'text-amber-700' : 'text-gray-900'}`}>{value}</p>
      <p className="mt-0.5 text-xs text-gray-500">{label}</p>
    </div>
  )
}
